// Palestinian National ID validator.
// Spec: 9 digits; the last digit is a Luhn check digit. Numbers shorter
// than 9 digits are zero-padded from the left before checksum.

export type IdValidationCode =
  | 'required'
  | 'nonNumeric'
  | 'tooLong'
  | 'allSame'
  | 'blocklisted'
  | 'invalidChecksum';

export interface IdValidationResult {
  ok: boolean;
  normalized: string;
  code?: IdValidationCode;
}

const TEST_BLOCKLIST = new Set([
  '123456789',
  '987654321',
  '000000018',
  '012345678',
]);

export function stripIdFormatting(raw: string): string {
  return raw.replace(/[\s\-.]/g, '');
}

export function sanitizeIdInput(raw: string): string {
  return stripIdFormatting(raw).replace(/\D/g, '').slice(0, 9);
}

export function padId(digits: string): string {
  return digits.padStart(9, '0');
}

export function isLuhnValid(id9: string): boolean {
  if (!/^\d{9}$/.test(id9)) return false;
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    const digit = Number(id9[i]);
    const factor = i % 2 === 0 ? 1 : 2;
    let product = digit * factor;
    if (product > 9) product -= 9;
    sum += product;
  }
  return sum % 10 === 0;
}

export function validatePalestinianId(raw: string): IdValidationResult {
  const trimmed = raw.trim();
  if (!trimmed) return { ok: false, normalized: '', code: 'required' };

  const stripped = stripIdFormatting(trimmed);
  if (!/^\d+$/.test(stripped)) {
    return { ok: false, normalized: stripped, code: 'nonNumeric' };
  }
  if (stripped.length > 9) {
    return { ok: false, normalized: stripped, code: 'tooLong' };
  }

  const padded = padId(stripped);

  if (/^(\d)\1{8}$/.test(padded)) {
    return { ok: false, normalized: padded, code: 'allSame' };
  }
  if (TEST_BLOCKLIST.has(padded)) {
    return { ok: false, normalized: padded, code: 'blocklisted' };
  }
  if (!isLuhnValid(padded)) {
    return { ok: false, normalized: padded, code: 'invalidChecksum' };
  }
  return { ok: true, normalized: padded };
}

export function maskId(id9: string): string {
  if (id9.length < 4) return '****';
  return '*'.repeat(Math.max(id9.length - 4, 0)) + id9.slice(-4);
}
